<template>
    <section class="question-builder">
  
      <header class="builder-header">
        <div>
          <h4>
            {{ title }}
          </h4>
  
          <p>
            {{ description }}
          </p>
        </div>
  
        <div class="header-actions">
          <button
            v-if="showBaseQuestionAction"
            type="button"
            class="base-button"
            @click="$emit('restore-base')"
          >
            Restore Base Questions
          </button>
  
        </div>
      </header>
  
      <div
        v-if="questions.length === 0"
        class="empty-state"
      >
        <p>
          No questions have been added.
        </p>
  
        <button
          v-if="showBaseQuestionAction"
          type="button"
          class="base-button"
          @click="$emit('restore-base')"
        >
          Add Base Questions
        </button>
      </div>
  
      <div
        v-for="(question, index) in questions"
        :key="question.id"
        class="question-card"
      >
        <div class="question-card-header">
          <div class="question-position">
            <span class="question-number">
              {{ index + 1 }}
            </span>
  
            <span>
              Question
            </span>
          </div>
  
          <div class="question-actions">
            <button
              type="button"
              class="move-button"
              :disabled="index === 0"
              aria-label="Move question up"
              @click="$emit('move-up', index)"
            >
              ↑
            </button>
  
            <button
              type="button"
              class="move-button"
              :disabled="index === questions.length - 1"
              aria-label="Move question down"
              @click="$emit('move-down', index)"
            >
              ↓
            </button>
  
            <button
              type="button"
              class="remove-button"
              @click="$emit('remove', index)"
            >
              Remove
            </button>
          </div>
        </div>
  
        <label class="field-group">
          <span>
            Question text
          </span>
  
          <textarea
            v-model="question.question"
            rows="3"
            placeholder="Enter the question shown to the user"
          />
        </label>
  
        <div class="options-grid">
          <label class="field-group">
            <span>
              Response type
            </span>
  
            <select v-model="question.responseType">
              <option value="Keyboard">
                Text
              </option>
  
              <option value="LongText">
                Long text
              </option>
  
              <option value="Number">
                Number
              </option>
  
              <option value="Date">
                Date
              </option>
  
              <option value="YesNo">
                Yes / No
              </option>
  
              <option value="Options">
                Options
              </option>
            </select>
          </label>
  
          <label class="required-setting">
            <input
              type="checkbox"
              :checked="!question.isRequired"
              @change="question.isRequired = !$event.target.checked"
            />
  
            <span>
              Can be skipped
            </span>
          </label>
        </div>
  
        <label
          v-if="question.responseType === 'Options'"
          class="field-group"
        >
          <span>
            Options
          </span>
  
          <input
            :value="question.responseOptions.join(', ')"
            type="text"
            placeholder="Option one, Option two, Option three"
            @input="
              updateResponseOptions(
                question,
                $event.target.value
              )
            "
          />
  
          <small>
            Separate options with commas.
          </small>
        </label>
      </div>

      <button
        type="button"
        class="add-button add-question-row"
        @click="$emit('add')"
      >
        Add Question
      </button>
    </section>
  </template>
  
  <script>
  export default {
    name:
      "WatchlistQuestionBuilder",
  
    props: {
      title: {
        type:
          String,
        default:
          "Watchlist Questions"
      },
  
      description: {
        type:
          String,
        default:
          "Questions used to understand what happened and identify possible patterns."
      },
  
      questions: {
        type:
          Array,
        required:
          true
      },
  
      showBaseQuestionAction: {
        type:
          Boolean,
        default:
          true
      }
    },
  
    emits: [
      "add",
      "remove",
      "move-up",
      "move-down",
      "restore-base"
    ],
  
    setup() {
  
      const updateResponseOptions =
        (
          question,
          value
        ) => {
  
          question.responseOptions =
            String(
              value ?? ""
            )
              .split(",")
              .map(
                option =>
                  option.trim()
              )
              .filter(
                Boolean
              );
        };
  
      return {
        updateResponseOptions
      };
    }
  };
  </script>
  
  <style scoped>
  .question-builder {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 20px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .builder-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  }
  
  .builder-header h4 {
    margin: 0;
    color: #1f2b25;
    font-size: 1.05rem;
  }
  
  .builder-header p {
    max-width: 680px;
    margin: 6px 0 0;
    color: #747e79;
    line-height: 1.45;
  }
  
  .header-actions {
    display: flex;
    flex-shrink: 0;
    gap: 10px;
  }
  
  .base-button,
  .add-button {
    padding: 9px 14px;
    border-radius: 9px;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .base-button {
    border: 1px solid #d4ddd8;
    background: #ffffff;
    color: #34423b;
  }
  
  .base-button:hover {
    background: #f4f7f5;
  }
  
  .add-button {
    border: 1px solid #c8ded2;
    background: #edf7f1;
    color: #178554;
  }
  
  .add-button:hover {
    background: #dff1e7;
  }

  .add-question-row {
    width: 100%;
  }
  
  .empty-state {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 22px;
    border: 1px dashed #d5ddd8;
    border-radius: 10px;
    color: #7c8681;
  }
  
  .empty-state p {
    margin: 0;
  }
  
  .question-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 18px;
    border: 1px solid #e4e9e6;
    border-radius: 11px;
    background: #fbfcfb;
  }
  
  .question-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  
  .question-position {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #334038;
    font-weight: 700;
  }
  
  .question-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 27px;
    height: 27px;
    border-radius: 50%;
    background: #edf7f1;
    color: #178554;
    font-size: 0.82rem;
  }
  
  .question-actions {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  
  .move-button {
    width: 34px;
    height: 32px;
    border: 1px solid #d7ded9;
    border-radius: 7px;
    background: #ffffff;
    color: #47534d;
    font: inherit;
    cursor: pointer;
  }
  
  .move-button:hover:not(:disabled) {
    background: #f1f5f2;
  }
  
  .move-button:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
  
  .remove-button {
    padding: 7px 11px;
    border: 1px solid #efc5c5;
    border-radius: 7px;
    background: #fff7f7;
    color: #bd3d3d;
    font: inherit;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
  }
  
  .remove-button:hover {
    background: #ffeded;
  }
  
  .field-group {
    display: flex;
    flex-direction: column;
    gap: 7px;
    color: #35423b;
    font-size: 0.9rem;
    font-weight: 600;
  }
  
  .field-group input,
  .field-group textarea,
  .field-group select {
    width: 100%;
    box-sizing: border-box;
    padding: 11px 12px;
    border: 1px solid #d7ded9;
    border-radius: 8px;
    background: #ffffff;
    color: #1f2a25;
    font: inherit;
  }
  
  .field-group textarea {
    resize: vertical;
    line-height: 1.45;
  }
  
  .field-group input:focus,
  .field-group textarea:focus,
  .field-group select:focus {
    border-color: #18ab69;
    outline: 3px solid rgba(24, 171, 105, 0.12);
  }
  
  .field-group small {
    color: #818b86;
    font-size: 0.78rem;
    font-weight: 400;
  }
  
  .options-grid {
    display: grid;
    grid-template-columns:
      minmax(200px, 1fr)
      minmax(180px, 0.45fr);
    gap: 16px;
  }
  
  .required-setting {
    display: flex;
    align-items: center;
    gap: 10px;
    align-self: end;
    min-height: 44px;
    padding: 0 14px;
    border: 1px solid #dfe5e1;
    border-radius: 8px;
    background: #ffffff;
    color: #35423b;
    font-weight: 600;
    cursor: pointer;
  }
  
  .required-setting input {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: #18ab69;
  }
  
  @media (max-width: 780px) {
    .builder-header,
    .empty-state {
      align-items: stretch;
      flex-direction: column;
    }
  
    .header-actions {
      width: 100%;
    }
  
    .base-button,
    .add-button {
      flex: 1;
    }
  
    .options-grid {
      grid-template-columns: 1fr;
    }
  
    .required-setting {
      align-self: stretch;
    }
  }
  
  @media (max-width: 520px) {
    .question-card-header {
      align-items: flex-start;
      flex-direction: column;
    }
  
    .question-actions {
      width: 100%;
    }
  
    .remove-button {
      margin-left: auto;
    }
  
    .header-actions {
      flex-direction: column;
    }
  }
  </style>
